/* @jsx jsx */
import { jsx, css } from '@emotion/react';
import { useCallback, useEffect, useRef } from 'react';
import { bind } from 'bind-event-listener';
import { N800 } from '@atlaskit/theme/colors';
import { ReactRenderer } from '@atlaskit/renderer';
import { useAnalyticsFlow } from '../../../analytics/analytics-flow/analyticsFlowUtils';
import { isPartialCopy, isSelectionWithinElement } from '../../../experience-application/screens-with-logic/utils/copy-response';
export const PreviewRenderer = ({
  jsonDocument,
  showTelepointer,
  providerFactory
}) => {
  const analyticsFlow = useAnalyticsFlow();
  const previewRendererRef = useRef(null);
  const copiedOnceRef = useRef(false);
  const onCopyHandler = useCallback(async () => {
    const previewRendererElement = previewRendererRef.current;
    if (!analyticsFlow || !previewRendererElement) {
      return;
    }
    if (!isSelectionWithinElement(previewRendererElement)) {
      return;
    }
    if (isPartialCopy(previewRendererRef.current)) {
      analyticsFlow.addAttributes({
        contentCopiedPartially: true
      });
    }

    // exit if copied already for this preview
    if (copiedOnceRef.current) {
      return;
    }
    analyticsFlow.addAttributes({
      contentCopied: true
    });
    copiedOnceRef.current = true;
  }, [analyticsFlow]);
  useEffect(() => {
    const unbind = bind(document, {
      type: 'copy',
      listener: onCopyHandler
    });
    return unbind;
  }, [onCopyHandler, analyticsFlow]);

  // override renderer's syntax highlighting styles so it appears consistent with the editor
  const styles = css({
    '.code-block .token.token': {
      fontStyle: 'normal',
      fontWeight: 'normal',
      color: `var(--ds-text, ${N800})`
    }
  });
  return jsx("div", {
    "data-testid": "preview-renderer",
    css: styles,
    ref: previewRendererRef
  }, jsx(ReactRenderer, {
    document: jsonDocument,
    media: {
      // We don't currently support images with captions
      allowLinking: false
    }
    // By default -- the renderer uses a "default schema" which supports all
    // the nodes and marks the markdown transformer can create.
    // see packages/editor/adf-schema/src/schema/default-schema.ts for details.
    ,
    useSpecBasedValidator: true,
    addTelepointer: showTelepointer,
    dataProviders: providerFactory
  }));
};